import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Move, SlidersHorizontal } from 'lucide-react'
import {
  CENTERED_FOCUS,
  DEFAULT_OP,
  FOCUS_STEP,
  MAX_BOX,
  MIN_BOX,
  SIZING_STEP,
  focusPoint,
  focusPosition,
  isRatioLocked,
  ratioForcedByOp,
  resolveBox,
  setBox,
  setFocus,
  sizingOf,
  snap,
  sourceRatio,
  supportsFocus,
  withSizing,
} from './editable/imageSizing'

/**
 * ImageSizingRail — la poignée de redimensionnement d'une image, et l'engrenage
 * qui ouvre ses réglages de cadrage.
 *
 * Elle n'existe qu'au survol (ou au focus) de l'image, dans la livrée du rail de
 * ligne : le builder n'allume aucun contrôle en permanence, et une pastille
 * posée à côté du contenu élargirait le bloc en édition.
 *
 * Ce qu'elle écrit : la boîte de LA SEULE bande prévisualisée (`device`). C'est
 * ça, « redimensionner par point de rupture » — on bascule d'appareil dans la
 * barre du builder, et on règle la bande qu'on voit.
 *
 * Elle porte aussi le POINT D'INTÉRÊT : sur un cadrage qui découpe, on déplace
 * l'image DANS son cadre en la faisant glisser (ou aux flèches, depuis la
 * pastille de déplacement). Le geste s'attache à l'image elle-même — pas à une
 * surface posée par-dessus, qui lui volerait le clic qui ouvre la médiathèque :
 * il ne devient un déplacement qu'au-delà d'un seuil, et c'est seulement alors
 * que le clic qui suit est avalé. Ce qui bouge à l'écran pendant le glissé,
 * c'est `object-position` ; ce qui est écrit au relâchement, c'est
 * `sizing.focus`, que le serveur retrouve dans le nom de la dérivée.
 *
 * Ce qu'elle NE fait pas : générer une dérivée par pixel parcouru. Le glissé est
 * prévisualisé en CSS sur l'élément lui-même, et la valeur n'est écrite qu'au
 * RELÂCHEMENT ; au clavier, après une courte inactivité. Chaque écriture change
 * l'URL de la dérivée, donc fabrique un fichier sur le serveur : le pas de 20 px
 * (celui du quantizer d'URL) est ce qui borne leur nombre.
 *
 * ---------------------------------------------------------------------------
 * Où elle est rendue, et pourquoi ça compte
 *
 * Dans un portail posé JUSTE APRÈS le champ (un `<span display:contents>` glissé
 * en frère), et non dans `<body>` : c'est ce qui met la poignée et l'engrenage à
 * leur place dans l'ordre de tabulation — au clavier, on atteint l'image, puis
 * ses réglages, puis la suite. Un portail vers `<body>` les aurait relégués en
 * fin de document, hors d'atteinte en pratique (RGAA 12.8).
 *
 * Contrepartie : rendue dans le bloc, elle peut tomber sous un ancêtre
 * TRANSFORMÉ (une cellule de tableau de bord, positionnée par
 * react-grid-layout), qui devient alors le bloc conteneur d'un `position:fixed`.
 * D'où `containingRect()` : les coordonnées sont exprimées par rapport à cet
 * ancêtre quand il existe, et par rapport à la fenêtre sinon.
 */

const KEY_COMMIT_DELAY = 400
const DEVICE_LABELS = { mobile: 'Mobile', tablet: 'Tablette', desktop: 'Ordinateur' }

/**
 * Le seuil (en pixels) au-delà duquel un appui sur l'image devient un
 * déplacement. En deçà, c'est un clic : il continue d'ouvrir l'éditeur du
 * champ. C'est cette tolérance qui permet au geste de vivre SUR l'image sans
 * lui prendre son clic.
 */
const PAN_THRESHOLD = 4

/**
 * Les variables de la PRÉVISUALISATION — distinctes de celles que React pose
 * (`--bimg-w`, `--bimg-ar`, …, cf. `sizingVars`). La recette les lit en
 * priorité : `width: min(100%, var(--bimg-pw, var(--bimg-w)))`.
 *
 * C'est tout le sujet : React DIFFE son style clé par clé et ne réécrit pas une
 * clé inchangée. Peindre le glissé sur SES variables, puis les retirer, laissait
 * l'image sans boîte au simple survol-puis-sortie ; les repeindre depuis la prop
 * du rail était pire — elle est périmée dès que l'écriture est passée par le
 * store de l'unité, qui ne re-rend pas ce composant. En n'écrivant que des
 * variables que React ne connaît pas, il n'y a plus rien à restaurer : les
 * retirer redonne exactement la valeur rendue.
 */
const PREVIEW_VARS = ['--bimg-pw', '--bimg-par']

/** Idem pour le point d'intérêt : la position peinte pendant le glissé. */
const POSITION_VAR = '--bimg-ppos'

/**
 * Peindre la boîte en cours de glissé, sans passer par le store. Une seule paire
 * de variables suffit : elle gagne dans TOUTES les bandes, donc la requête de
 * conteneur active ne peut pas continuer à servir l'ancienne valeur.
 */
const paintPreview = (el, box) => {
  if (!el) return
  el.classList.add('builder-img-sized')
  el.style.setProperty('--bimg-pw', `${box.w}px`)
  el.style.setProperty('--bimg-par', `${box.w} / ${box.h}`)
}

/**
 * Retirer la prévisualisation : l'élément retrouve la boîte que React a rendue.
 * La classe ne se retire que si React ne l'a pas mise lui-même — et c'est SA
 * variable `--bimg-w-desktop` qui le dit : plus personne d'autre ne l'écrit.
 */
const clearPreview = (el) => {
  if (!el) return
  for (const name of PREVIEW_VARS) el.style.removeProperty(name)
  if (!el.style.getPropertyValue('--bimg-w-desktop')) el.classList.remove('builder-img-sized')
}

/**
 * Peindre le cadrage en cours de glissé. La classe est posée ici quand React ne
 * l'a pas mise : la première fois qu'on déplace une image, elle n'a pas encore
 * de point d'intérêt, donc pas de recette `object-position` à détourner.
 */
const paintPosition = (el, point) => {
  if (!el) return
  el.classList.add('builder-img-pos')
  el.style.setProperty(POSITION_VAR, focusPosition(point))
}

/** Retirer la prévisualisation de cadrage — cf. `clearPreview`. */
const clearPosition = (el) => {
  if (!el) return
  el.style.removeProperty(POSITION_VAR)
  if (!el.style.getPropertyValue('--bimg-pos')) el.classList.remove('builder-img-pos')
}

/**
 * Le DÉBORD de l'image dans son cadre, en pixels : de combien elle peut
 * glisser sur chaque axe. C'est le dénominateur du geste — sans lui, on ne sait
 * pas combien de pourcents vaut un pixel parcouru.
 *
 * Il se lit sur l'élément rendu (`object-fit` calculé + taille naturelle du
 * fichier servi), et non sur la valeur : en édition le canevas sert l'image
 * ENTIÈRE justement pour qu'il y ait un débord à parcourir, et une image déjà
 * découpée par le serveur n'en a aucun.
 */
const panOverflow = (el) => {
  if (!el) return null
  const rect = el.getBoundingClientRect()
  const nw = el.naturalWidth
  const nh = el.naturalHeight
  if (!rect.width || !rect.height || !nw || !nh) return null
  const fit = getComputedStyle(el).objectFit
  let scale
  if (fit === 'none') scale = 1
  else if (fit === 'contain' || fit === 'scale-down') scale = Math.min(rect.width / nw, rect.height / nh)
  else if (fit === 'fill') scale = 0
  else scale = Math.max(rect.width / nw, rect.height / nh)
  return {
    x: Math.max(0, nw * scale - rect.width),
    y: Math.max(0, nh * scale - rect.height),
  }
}

/** Un pourcentage de cadrage : borné, et calé sur le pas partagé du serveur. */
const snapPercent = (value) => Math.min(100, Math.max(0,
  Math.round(value / FOCUS_STEP) * FOCUS_STEP))

/**
 * Avaler LE clic qui suit un glissé. Sans ça, relâcher le pointeur sur l'image
 * ouvrirait l'éditeur du champ juste après l'avoir recadrée. Le retrait
 * différé évite qu'un geste terminé hors de la fenêtre (aucun clic derrière)
 * ne laisse un piège qui mangerait le clic suivant.
 */
const swallowNextClick = () => {
  const stop = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  document.addEventListener('click', stop, { capture: true, once: true })
  setTimeout(() => document.removeEventListener('click', stop, { capture: true }), 300)
}

/**
 * Le bloc conteneur d'un `position: fixed` : la fenêtre, SAUF sous un ancêtre
 * transformé (ou filtré, ou en `will-change: transform`), qui le remplace.
 */
const containingRect = (node) => {
  let el = node?.parentElement
  while (el && el !== document.body) {
    const cs = getComputedStyle(el)
    if (cs.transform !== 'none' || cs.filter !== 'none' || cs.perspective !== 'none'
      || cs.willChange.includes('transform')) {
      return el.getBoundingClientRect()
    }
    el = el.parentElement
  }
  return null
}

export const ImageSizingRail = ({ target, device, value, readValue, onCommit, onOpenSettings, onBusyChange }) => {
  const { imgEl, fieldEl } = target
  const [host] = useState(() => document.createElement('span'))
  const [rect, setRect] = useState(null)
  // La boîte affichée par la pastille : celle en cours de geste, ou celle du
  // store. `null` = rien en cours.
  const [draft, setDraft] = useState(null)
  const hintId = useId()
  const dragRef = useRef(null)
  const keyTimer = useRef(null)
  // Le rapport tenu pendant une SÉQUENCE de flèches. Le recalculer à chaque
  // appui le ferait dériver : chaque côté déduit est calé sur le pas de 20 px,
  // donc le rapport de la boîte obtenue n'est jamais tout à fait celui d'avant.
  const keyRatio = useRef(0)
  const sizing = sizingOf(value)
  const storedBox = resolveBox(sizing, device)
  const op = sizing?.op ?? DEFAULT_OP
  // Redimensionner à la main GARDE LES PROPORTIONS par défaut : c'est ce qui
  // évite de déformer une image sans le vouloir. « Délier » (dans les réglages)
  // libère les deux côtés — c'est ce qui permet un portrait sur mobile et un
  // panoramique sur ordinateur à partir de la même photo. Le style « Contenir »
  // ne se délie pas : le serveur y garde le rapport de la source de toute façon,
  // une autre boîte n'ajouterait que des bandes vides.
  const locked = isRatioLocked(sizing)
  // Sous « Contenir », le rapport à tenir est celui de l'ORIGINAL ; sinon c'est
  // celui de la boîte au moment où le geste commence.
  const forcedRatio = ratioForcedByOp(op) ? sourceRatio(value) : 0

  // --- déplacer l'image dans son cadre --------------------------------------
  // Une source non recadrable (URL locale nue) garde sa boîte CSS mais AUCUNE
  // dérivée ne sera produite : le fichier téléchargé reste l'original.
  const croppable = imgEl?.getAttribute('data-builder-img') !== 'static'
  // Le style de cadrage EFFECTIF : celui du réglage, sinon celui que le YAML du
  // bloc déclare — que ce composant ne lit pas, mais que `LazyImage` expose sur
  // l'élément. Sans lui, viser une image jamais redimensionnée écrirait un
  // point d'intérêt sous « Contenir », qui n'en veut pas, et le réglage
  // disparaîtrait sans erreur.
  const effectiveOp = sizing?.op ?? imgEl?.getAttribute('data-builder-op') ?? DEFAULT_OP
  // Déplacer n'a de sens que là où le cadre JETTE quelque chose. Une source non
  // recadrable en jette aussi, dès qu'une boîte la découpe en CSS.
  const canPan = supportsFocus(effectiveOp) && (croppable || Boolean(storedBox))
  // Le canevas sert-il l'image ENTIÈRE (le déplacement se voit sous la main) ou
  // la dérivée déjà découpée (il n'apparaît qu'au relâchement, le temps que le
  // serveur la regénère) ? Le second cas arrive quand la valeur ne porte pas
  // les dimensions de la source — il n'y a alors rien à calculer, et le dire
  // vaut mieux que laisser croire à une panne.
  const liveFraming = imgEl?.getAttribute('data-builder-frame') === 'live'
  const [panDraft, setPanDraft] = useState(null)
  const panRef = useRef(null)
  const panKeyTimer = useRef(null)
  const panHintId = useId()

  const commitFocus = useCallback((point) => {
    // Même précaution que `commit` : la valeur est RELUE au moment d'écrire.
    const live = readValue()
    clearPosition(imgEl)
    onCommit(withSizing(live, setFocus(sizingOf(live), point, effectiveOp)))
  }, [readValue, imgEl, onCommit, effectiveOp])

  const engagePan = useCallback((e) => {
    const pan = panRef.current
    if (!pan || pan.moved) return
    pan.moved = true
    pan.captureEl?.setPointerCapture?.(e.pointerId)
    imgEl?.classList.add('builder-img-panning')
    onBusyChange?.(true)
  }, [imgEl, onBusyChange])

  /** Armer un geste. `immediate` : depuis la pastille, il n'y a pas de clic à
   *  préserver, le déplacement commence au premier pixel. */
  const startPan = useCallback((e, captureEl, immediate) => {
    if (e.button != null && e.button !== 0) return
    const overflow = panOverflow(imgEl)
    // Rien ne dépasse du cadre : il n'y a rien à déplacer, et surtout pas de
    // clic à confisquer.
    if (!overflow || (!overflow.x && !overflow.y)) return
    panRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      captureEl,
      overflow,
      point: focusPoint(sizingOf(readValue())),
      last: null,
      moved: false,
    }
    if (immediate) engagePan(e)
  }, [imgEl, readValue, engagePan])

  const movePan = useCallback((e) => {
    const pan = panRef.current
    if (!pan || e.pointerId !== pan.id) return
    const dx = e.clientX - pan.x
    const dy = e.clientY - pan.y
    if (!pan.moved) {
      if (Math.abs(dx) < PAN_THRESHOLD && Math.abs(dy) < PAN_THRESHOLD) return
      engagePan(e)
    }
    e.preventDefault()
    // Le pointeur pousse l'IMAGE : la tirer vers la droite découvre sa partie
    // gauche, donc fait DÉCROÎTRE le pourcentage. Un axe sans débord ne bouge
    // pas — c'est ce qui rend le geste sûr sur un cadrage qui ne rogne qu'un
    // seul côté.
    const point = {
      x: pan.overflow.x ? snapPercent(pan.point.x - (dx / pan.overflow.x) * 100) : pan.point.x,
      y: pan.overflow.y ? snapPercent(pan.point.y - (dy / pan.overflow.y) * 100) : pan.point.y,
    }
    pan.last = point
    setPanDraft(point)
    paintPosition(imgEl, point)
  }, [engagePan, imgEl])

  const endPan = useCallback((e) => {
    const pan = panRef.current
    if (!pan || (e.pointerId != null && e.pointerId !== pan.id)) return
    panRef.current = null
    imgEl?.classList.remove('builder-img-panning')
    if (!pan.moved) return
    pan.captureEl?.releasePointerCapture?.(e.pointerId)
    // Le clic qui suit le relâchement rouvrirait l'éditeur du champ.
    swallowNextClick()
    setPanDraft(null)
    onBusyChange?.(false)
    if (pan.last) commitFocus(pan.last)
  }, [imgEl, onBusyChange, commitFocus])

  // Le geste vit SUR l'image, en écouteurs natifs : c'est le seul endroit d'où
  // il ne prend le clic à personne (au-delà du seuil seulement), et le seul où
  // le curseur peut annoncer qu'elle se déplace.
  useEffect(() => {
    if (!imgEl || !canPan) return
    // Le curseur ne promet un déplacement que s'il y a VRAIMENT un débord : une
    // image que son cadre n'entame pas (elle remplit sa boîte pile) ne bouge
    // pas, et un `grab` qui ne fait rien est un défaut à lui seul. Le débord se
    // mesure sur l'élément rendu, donc pas avant que le fichier soit chargé.
    const refreshAffordance = () => {
      const overflow = panOverflow(imgEl)
      imgEl.classList.toggle('builder-img-pannable', Boolean(overflow && (overflow.x || overflow.y)))
    }
    refreshAffordance()
    imgEl.addEventListener('load', refreshAffordance)
    const onDown = (e) => {
      // Ctrl/Cmd + clic reste un clic (menu contextuel, ouverture annexe).
      if (e.ctrlKey || e.metaKey) return
      startPan(e, imgEl, false)
    }
    // Le glisser-déposer natif d'une image partirait au premier pixel et
    // emporterait le geste avec lui.
    const onDragStart = (e) => e.preventDefault()
    imgEl.addEventListener('pointerdown', onDown)
    imgEl.addEventListener('pointermove', movePan)
    imgEl.addEventListener('pointerup', endPan)
    imgEl.addEventListener('pointercancel', endPan)
    imgEl.addEventListener('dragstart', onDragStart)
    return () => {
      imgEl.classList.remove('builder-img-pannable', 'builder-img-panning')
      imgEl.removeEventListener('load', refreshAffordance)
      imgEl.removeEventListener('pointerdown', onDown)
      imgEl.removeEventListener('pointermove', movePan)
      imgEl.removeEventListener('pointerup', endPan)
      imgEl.removeEventListener('pointercancel', endPan)
      imgEl.removeEventListener('dragstart', onDragStart)
    }
  }, [imgEl, canPan, startPan, movePan, endPan])

  /**
   * Au clavier, les flèches déplacent LE CADRE (droite = on voit plus la droite
   * de l'image), pas l'image : c'est la lecture d'une commande de position, et
   * un glissé n'est de toute façon pas disponible à tous les pointeurs
   * (WCAG 2.5.7). `Origine` recentre.
   */
  const onPanKeyDown = useCallback((e) => {
    const axis = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key]
    const recenter = e.key === 'Home'
    if (!axis && !recenter) return
    e.preventDefault()
    e.stopPropagation()
    const base = panDraft ?? focusPoint(sizingOf(readValue()))
    const step = FOCUS_STEP * (e.altKey ? 4 : 1)
    const point = recenter
      ? { ...CENTERED_FOCUS }
      : { x: snapPercent(base.x + axis[0] * step), y: snapPercent(base.y + axis[1] * step) }
    setPanDraft(point)
    paintPosition(imgEl, point)
    clearTimeout(panKeyTimer.current)
    panKeyTimer.current = setTimeout(() => {
      commitFocus(point)
      setPanDraft(null)
    }, KEY_COMMIT_DELAY)
  }, [panDraft, readValue, imgEl, commitFocus])

  // Le portail : un frère du champ, transparent pour la mise en page.
  useLayoutEffect(() => {
    const anchor = fieldEl ?? imgEl
    if (!anchor?.isConnected) return
    host.className = 'builder-img-rail-host'
    anchor.after(host)
    return () => host.remove()
  }, [host, fieldEl, imgEl])

  /** La boîte rendue, en crans : ce que l'éditeur voit réellement à l'écran. */
  const renderedBox = useCallback(() => {
    const box = imgEl?.getBoundingClientRect()
    if (!box || !box.width || !box.height) return null
    return { w: snap(box.width), h: snap(box.height) }
  }, [imgEl])

  /** Ce qu'affiche la pastille : le geste en cours, sinon la valeur réglée. */
  const currentBox = useCallback(
    () => draft ?? storedBox ?? renderedBox() ?? { w: 320, h: 200 },
    [draft, storedBox, renderedBox],
  )

  /**
   * La boîte de DÉPART d'un geste : toujours celle qui est rendue, jamais la
   * valeur stockée. Les deux diffèrent dès que la boîte est PLAFONNÉE par sa
   * colonne (`min(100%, …)`) — une image réglée à 680 px dans une colonne de
   * 325 px. Partir de 680 ferait grimper un chiffre que rien ne montre : le
   * pointeur avancerait, l'image ne bougerait plus. Partir du rendu recale du
   * même coup la dérivée sur la largeur réellement affichée, ce qui est tout
   * l'objet de la fonction.
   */
  const gestureBox = useCallback(
    () => draft ?? renderedBox() ?? storedBox ?? { w: 320, h: 200 },
    [draft, renderedBox, storedBox],
  )

  // Suivre la boîte de l'image : elle bouge au défilement, au redimensionnement
  // de la fenêtre, et à chaque fois que l'image elle-même change de taille.
  useLayoutEffect(() => {
    if (!imgEl) return
    const measure = () => {
      const box = imgEl.getBoundingClientRect()
      const base = containingRect(host)
      setRect({
        top: box.top - (base?.top ?? 0),
        left: box.left - (base?.left ?? 0),
        width: box.width,
        height: box.height,
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(imgEl)
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [imgEl, host])

  // Fin de vie : l'élément doit retrouver exactement la mise en page rendue —
  // une prévisualisation abandonnée ne laisse rien derrière elle.
  useEffect(() => () => {
    clearTimeout(keyTimer.current)
    clearTimeout(panKeyTimer.current)
    clearPreview(imgEl)
    clearPosition(imgEl)
  }, [imgEl])

  const commit = useCallback((box) => {
    // La valeur est relue AU MOMENT D'ÉCRIRE. La prop `value` peut être en
    // retard : quand le champ appartient à une unité, l'écriture va dans le
    // store de l'unité, que ce composant-ci n'observe pas — écrire par-dessus
    // une copie périmée effacerait le réglage d'une autre bande.
    keyRatio.current = 0
    const live = readValue()
    const next = setBox(sizingOf(live), device, box)
    clearPreview(imgEl)
    onCommit(withSizing(live, next))
  }, [onCommit, readValue, device, imgEl])

  // --- glisser ---------------------------------------------------------------

  const onHandlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    keyRatio.current = 0
    const start = gestureBox()
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      box: start,
      ratio: forcedRatio || start.w / start.h,
    }
    setDraft(start)
    onBusyChange?.(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [gestureBox, forcedRatio, onBusyChange])

  const onHandlePointerMove = useCallback((e) => {
    const drag = dragRef.current
    if (!drag) return
    const clamp = (n) => Math.min(MAX_BOX, Math.max(MIN_BOX, n))
    const w = clamp(snap(drag.box.w + (e.clientX - drag.x)))
    // Maj INVERSE le réglage courant : elle libère un rapport verrouillé, et
    // verrouille un rapport libre — le geste habituel des éditeurs, dans les
    // deux sens.
    const keepRatio = locked !== e.shiftKey
    const h = keepRatio
      ? clamp(snap(w / drag.ratio))
      : clamp(snap(drag.box.h + (e.clientY - drag.y)))
    const box = { w, h }
    setDraft(box)
    paintPreview(imgEl, box)
  }, [imgEl, locked])

  const endDrag = useCallback((e) => {
    if (!dragRef.current) return
    dragRef.current = null
    e?.currentTarget?.releasePointerCapture?.(e.pointerId)
    if (draft) commit(draft)
    setDraft(null)
    onBusyChange?.(false)
  }, [draft, commit, onBusyChange])

  // --- clavier ---------------------------------------------------------------
  // Un glissé n'est ni utilisable au clavier ni disponible à tous les pointeurs
  // (WCAG 2.5.7) : les flèches font le même travail, par crans.
  const onHandleKeyDown = useCallback((e) => {
    const axis = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key]
    if (!axis) return
    e.preventDefault()
    e.stopPropagation()
    const step = SIZING_STEP * (e.altKey ? 5 : 1)
    const base = gestureBox()
    const clamp = (n) => Math.min(MAX_BOX, Math.max(MIN_BOX, n))
    // Même règle qu'au pointeur : proportions tenues par défaut, Maj inverse.
    const keepRatio = locked !== e.shiftKey
    const ratio = forcedRatio || keyRatio.current || base.w / base.h
    keyRatio.current = ratio
    let w = base.w + axis[0] * step
    let h = base.h + axis[1] * step
    if (keepRatio) {
      // Le côté déduit est calé sur le pas : c'est lui qui partira dans l'URL.
      if (axis[0]) h = snap(w / ratio)
      else w = snap(h * ratio)
    }
    const box = { w: clamp(w), h: clamp(h) }
    setDraft(box)
    paintPreview(imgEl, box)
    clearTimeout(keyTimer.current)
    keyTimer.current = setTimeout(() => {
      commit(box)
      setDraft(null)
    }, KEY_COMMIT_DELAY)
  }, [gestureBox, imgEl, commit, locked, forcedRatio])

  if (!rect) return null
  const box = currentBox()
  const deviceLabel = DEVICE_LABELS[device] ?? device
  const inherited = !storedBox && !draft
  const keepsRatio = locked

  return createPortal(
    <div
      className="builder-img-rail"
      style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
    >
      <span className="builder-img-rail-frame" aria-hidden="true" />
      {/* Au repos, un seul PICTO. La mesure ne s'affiche que pendant le geste :
          au survol, elle n'apprenait rien (l'éditeur voit la taille, il la
          règle) et posait un pavé de chiffres sur son image. Pendant le glissé
          au contraire elle est la seule confirmation de ce qu'on est en train
          de faire — et la seule qu'un lecteur d'écran reçoive, d'où la région
          `status`, montée en permanence pour que ses mises à jour soient
          annoncées. */}
      <div className="builder-img-rail-bar" data-sizing={(draft || panDraft) ? 'on' : 'off'}>
        <span className="builder-img-rail-size" role="status">
          {draft && (
            <>
              {draft.w} × {draft.h}
              <span className="builder-img-rail-device">
                {deviceLabel}{keepsRatio ? ' · proportions liées' : ''}
              </span>
            </>
          )}
          {!draft && panDraft && (
            <>
              {panDraft.x} % · {panDraft.y} %
              <span className="builder-img-rail-device">cadrage</span>
            </>
          )}
        </span>
        {canPan && (
          <button
            type="button"
            className="builder-row-action"
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              startPan(e, e.currentTarget, true)
            }}
            onPointerMove={movePan}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            onKeyDown={onPanKeyDown}
            onClick={(e) => e.stopPropagation()}
            aria-roledescription="poignée de cadrage"
            aria-describedby={panHintId}
            title={'Déplacer l’image dans son cadre — ou faire glisser l’image elle-même'
              + (liveFraming ? '' : ' (le nouveau cadrage n’apparaît qu’au relâchement)')}
            aria-label="Déplacer l’image dans son cadre"
          >
            <Move size={14} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="builder-row-action"
          onClick={(e) => { e.stopPropagation(); onOpenSettings() }}
          title={`Taille et cadrage de l’image — ${deviceLabel}`
            + (inherited ? ' (hérité)' : '')
            + (croppable ? '' : ' · source non recadrable')}
          aria-label={`Taille et cadrage de l’image sur ${deviceLabel}`}
        >
          <SlidersHorizontal size={14} aria-hidden="true" />
        </button>
        {/* Le mode d'emploi de la poignée : un `title` n'est ni focalisable ni
            restitué par la plupart des lecteurs d'écran (Opquast 159). */}
        {canPan && (
          <span id={panHintId} className="sr-only">
            Flèches : déplacer le cadre par pas de {FOCUS_STEP} pour cent — droite montre
            davantage la droite de l’image. Alt + flèches : 4 pas. Origine : recentrer.
            L’image elle-même se déplace aussi au glissé.
            {liveFraming ? '' : ' Le nouveau cadrage n’apparaît qu’une fois le réglage relâché.'}
          </span>
        )}
        <span id={hintId} className="sr-only">
          Flèches : redimensionner par pas de {SIZING_STEP} pixels. Alt + flèches : 5 pas.
          {keepsRatio
            ? ' Les proportions sont liées ; Maj les libère le temps du geste.'
            : ' Les proportions sont libres ; Maj les tient le temps du geste.'}
          {croppable ? '' : ' Cette source n’est pas recadrable : seul l’affichage change.'}
        </span>
      </div>
      <button
        type="button"
        className="builder-img-rail-handle"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onHandleKeyDown}
        onClick={(e) => e.stopPropagation()}
        aria-roledescription="poignée de redimensionnement"
        aria-describedby={hintId}
        title={croppable
          ? `Redimensionner sur ${deviceLabel} — ${box.w} × ${box.h}`
            + (keepsRatio ? ' (proportions liées, Maj pour libérer)' : ' (proportions libres, Maj pour lier)')
          : `Redimensionner l’affichage sur ${deviceLabel} — cette source n’est pas recadrable, le fichier téléchargé ne changera pas`}
        aria-label={`Redimensionner l’image sur ${deviceLabel} — ${box.w} sur ${box.h} pixels`}
      />
    </div>,
    host,
  )
}
